// Centralized configuration for all URLs and endpoints
// This file allows easy management of all server addresses

// Smart API URL detection for both local and internet access
function detectApiUrl(): string {
  // If environment variable is set, use it (e.g., /api for relative paths)
  if ((import.meta as any).env?.VITE_API_URL) {
    const viteApiUrl = (import.meta as any).env.VITE_API_URL;
    
    // If it's a relative path (like /api), prepend current origin
    if (viteApiUrl.startsWith('/')) {
      return window.location.origin;
    }
    
    return viteApiUrl;
  }
  
  // Fallback: use current origin (includes protocol, hostname, and port)
  // This ensures API requests go to the same origin, avoiding CORS issues
  // Nginx in the frontend container proxies /api/ to backend:5000
  return window.location.origin;
}

function detectFrontendUrl(): string {
  // If environment variable is set, use it
  if ((import.meta as any).env?.VITE_FRONTEND_URL) {
    return (import.meta as any).env.VITE_FRONTEND_URL;
  }
  
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;
  
  // Use current location's port (8080 in production, 5173 in dev, etc.)
  return `${protocol}//${hostname}${port ? ':' + port : ''}`;
}

// Get the actual URLs
const API_BASE_URL = detectApiUrl();
const FRONTEND_BASE_URL = detectFrontendUrl();

// Debug logging
console.log('🔧 SpellBook Config Debug:');
console.log('📍 Current hostname:', window.location.hostname);
console.log('📍 Current protocol:', window.location.protocol);
console.log('📍 Current port:', window.location.port);
console.log('🌐 Detected API Base URL:', API_BASE_URL);
console.log('🌐 Detected Frontend Base URL:', FRONTEND_BASE_URL);
console.log('🌍 Access type:', window.location.hostname.match(/^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/) ? 'Local Network' : 'Internet/External');

// API Configuration
export const API_CONFIG = {
  BASE_URL: API_BASE_URL,
  API_URL: `${API_BASE_URL}/api/v1`,
  
  // Auth endpoints
  AUTH: {
    LOGIN: `${API_BASE_URL}/api/v1/auth/login`,
    USERS: `${API_BASE_URL}/api/v1/auth/users`,
    MANAGEMENT_USERS: `${API_BASE_URL}/api/v1/auth/users/management`,
    ME: `${API_BASE_URL}/api/v1/auth/me`,
    PASSWORD: `${API_BASE_URL}/api/v1/auth/me/password`,
    FORGOT_PASSWORD: `${API_BASE_URL}/api/v1/auth/forgot-password`,
    RESET_PASSWORD: `${API_BASE_URL}/api/v1/auth/reset-password`,
  },
  
  // Artist endpoints
  ARTISTS: {
    LIST: `${API_BASE_URL}/api/v1/artists`,
    CREATE: `${API_BASE_URL}/api/v1/artists`,
    UPDATE: (id: string) => `${API_BASE_URL}/api/v1/artists/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/api/v1/artists/${id}`,
  },
  
  // Project endpoints
  PROJECTS: {
    LIST: `${API_BASE_URL}/api/v1/projects`,
    CREATE: `${API_BASE_URL}/api/v1/projects`,
    UPDATE: (id: string) => `${API_BASE_URL}/api/v1/projects/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/api/v1/projects/${id}`,
    ASSIGN_ARTISTS: (id: string) => `${API_BASE_URL}/api/v1/projects/${id}/artists`,
  },
  
  // User management endpoints
  USERS: {
    LIST: `${API_BASE_URL}/api/v1/auth/users`,
    CREATE: `${API_BASE_URL}/api/v1/auth/users`,
    UPDATE: (id: string) => `${API_BASE_URL}/api/v1/auth/users/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/api/v1/auth/users/${id}`,
  },
  
  // Export endpoints
  EXPORT: {
    ARTISTS_CSV: `${API_BASE_URL}/api/v1/export/artists/csv`,
  },
  
  // Static file endpoints (no /api prefix!)
  STATIC: {
    AVATARS: `${API_BASE_URL}/api/uploads/avatars`,
    UPLOADS: `${API_BASE_URL}/api/uploads`,
  },
  
  // Health check
  HEALTH: `${API_BASE_URL}/api/v1/health`,
};

// Frontend Configuration
export const FRONTEND_CONFIG = {
  BASE_URL: FRONTEND_BASE_URL,
  PORT: (import.meta as any).env?.VITE_PORT || window.location.port || '8080',
  HOST: (import.meta as any).env?.VITE_HOST || window.location.hostname || 'localhost',
};

// Avatar Configuration
export const AVATAR_CONFIG = {
  // UI Avatars API
  UI_AVATARS_BASE: 'https://api.dicebear.com/8.x/adventurer/svg',
  UI_AVATARS_DEFAULT_SIZE: 200,
  UI_AVATARS_DEFAULT_BACKGROUND: '6366f1',
  UI_AVATARS_DEFAULT_COLOR: 'ffffff',
  
  // Local avatar serving
  LOCAL_AVATAR_BASE: `${API_BASE_URL}/api/uploads/avatars`,
  
  // Generate UI Avatar URL
  generateUIAvatar: (name: string, size: number = 200, background?: string, color?: string) => {
    const initials = name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
    
    const params = new URLSearchParams({
      name: initials,
      size: size.toString(),
      background: background || AVATAR_CONFIG.UI_AVATARS_DEFAULT_BACKGROUND,
      color: color || AVATAR_CONFIG.UI_AVATARS_DEFAULT_COLOR,
      format: 'png',
      bold: 'true'
    });
    
    return `${AVATAR_CONFIG.UI_AVATARS_BASE}?${params.toString()}`;
  },
  
  // Generate local avatar URL
  generateLocalAvatar: (filename: string) => {
    return `${AVATAR_CONFIG.LOCAL_AVATAR_BASE}/${filename}`;
  },
};

// Network Configuration
export const NETWORK_CONFIG = {
  TIMEOUT: 10000, // 10 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
};

// Debug Configuration
export const DEBUG_CONFIG = {
  ENABLED: (import.meta as any).env?.VITE_DEBUG === 'true',
  LOG_API_CALLS: (import.meta as any).env?.VITE_LOG_API_CALLS === 'true',
  LOG_AVATAR_LOADING: (import.meta as any).env?.VITE_LOG_AVATAR_LOADING === 'true',
};

// SSO Configuration - MagicBox integration
export const SSO_CONFIG = {
  MAGICBOX_URL: (import.meta as any).env?.VITE_MAGICBOX_URL || '',
};

// Export API_URL and API_BASE_URL for backward compatibility
export const API_URL = API_BASE_URL;
export { API_BASE_URL };

// Helper functions
export const getApiUrl = (endpoint: string) => {
  return `${API_CONFIG.API_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
};

export const getStaticUrl = (path: string) => {
  return `${API_CONFIG.STATIC.UPLOADS}${path.startsWith('/') ? path : `/${path}`}`;
};

// Log configuration on load
if (DEBUG_CONFIG.ENABLED) {
  console.log('🔧 SpellBook Configuration:', {
    API_BASE_URL: API_CONFIG.BASE_URL,
    FRONTEND_BASE_URL: FRONTEND_CONFIG.BASE_URL,
    API_URL: API_CONFIG.API_URL,
    AVATAR_BASE: AVATAR_CONFIG.LOCAL_AVATAR_BASE,
  });
}
